/**
 * ethminions.php center-box (867×708) 좌표계 — boxt 프레임·디스플레이·버튼·하단 GIF(solo/team).
 */

import * as PIXI from 'pixi.js';
import { ladderRailEndsInParentRect } from '../shared/minionsLogic';
import type { MinionsTextureSet } from './minionsAssets';

export const ETH_FRAME_W = 867;
export const ETH_FRAME_H = 708;

const DISP_X = Math.round(ETH_FRAME_W * (1 - 0.85) * 0.5);
const DISP_Y = Math.round(ETH_FRAME_H * (1 - 0.8) * 0.5);
const DISP_W = Math.round(ETH_FRAME_W * 0.85);
const DISP_H = Math.round(ETH_FRAME_H * 0.8);

/** left/right/odd/even PNG 공통 표시 박스(설계 픽셀, 정사각형) — 사다리 선 끝 중심 */
const UNIFIED_GAME_BTN_BOX = 70;

/** ethminions.php `.waiting-scoreboard` — display-area 기준 top 64% 중앙 */
const SCOREBOARD_CENTER_X = DISP_X + DISP_W / 2;
const SCOREBOARD_CENTER_Y = DISP_Y + DISP_H * 0.64;
const SB_W = 320;
const SB_H = 138;

function sbLabel(
  content: string,
  fontSize: number,
  fill: number,
  wordWrapWidth?: number
): PIXI.Text {
  return new PIXI.Text(
    content,
    new PIXI.TextStyle({
      fontFamily: 'Pretendard, system-ui, -apple-system, Courier New, monospace',
      fontSize,
      fill,
      fontWeight: 'bold',
      align: 'center',
      wordWrap: wordWrapWidth != null,
      wordWrapWidth: wordWrapWidth ?? 300,
      lineHeight: Math.round(fontSize * 1.25),
    })
  );
}

function hiddenSprite(): PIXI.Sprite {
  const s = new PIXI.Sprite(PIXI.Texture.WHITE);
  s.visible = false;
  s.width = 1;
  s.height = 1;
  s.alpha = 0;
  return s;
}

function placeSprite(
  s: PIXI.Sprite,
  x: number,
  y: number,
  w: number,
  h: number
) {
  s.anchor.set(0, 0);
  s.scale.set(1, 1);
  s.position.set(x, y);
  s.alpha = 1;
  if (w > 0 && h > 0 && s.texture?.valid) {
    s.width = w;
    s.height = h;
  }
}

/** 사다리 선 끝(프레임 좌표)에 맞춤 — 동일 박스 안 비율 유지 */
function placeGameButtonAtRailEnd(
  sp: PIXI.Sprite,
  cx: number,
  cy: number,
  box: number
) {
  sp.anchor.set(0.5, 0.5);
  sp.position.set(cx, cy);
  sp.alpha = 1;
  if (!sp.texture?.valid || sp.texture.width < 1 || sp.texture.height < 1) {
    sp.scale.set(1, 1);
    return;
  }
  const tw = sp.texture.width;
  const th = sp.texture.height;
  const k = Math.min(box / tw, box / th);
  sp.scale.set(k, k);
}

export class MinionsEthDecor extends PIXI.Container {
  private frameFallback = new PIXI.Graphics();
  private frameSpr = hiddenSprite();
  private boardAnchor = new PIXI.Container();
  private leftSpr = hiddenSprite();
  private rightSpr = hiddenSprite();
  private oddSpr = hiddenSprite();
  private evenSpr = hiddenSprite();
  private soloSpr = hiddenSprite();
  private teamSpr = hiddenSprite();

  /** 대기 전광판 (display-area 중앙 하단 쪽) */
  private scoreboardRoot = new PIXI.Container();
  private scoreboardBg = new PIXI.Graphics();
  private scoreboardTitle = sbLabel('⏳ 다음 블록(홀수+2)', 15, 0xfef3c7);
  private scoreboardBlock = sbLabel(
    '다음 기다리는 블록 번호: 불러오는 중...',
    22,
    0xfacc15,
    SB_W - 24
  );
  private scoreboardCountdown = sbLabel('로딩중...', 17, 0xfbbf24, SB_W - 20);

  constructor() {
    super();
    this.sortableChildren = true;
    this.addChild(this.frameFallback);
    this.addChild(this.frameSpr);
    this.addChild(this.boardAnchor);
    this.addChild(this.leftSpr);
    this.addChild(this.rightSpr);
    this.addChild(this.oddSpr);
    this.addChild(this.evenSpr);
    this.addChild(this.soloSpr);
    this.addChild(this.teamSpr);

    this.scoreboardRoot.zIndex = 200;
    this.scoreboardRoot.position.set(SCOREBOARD_CENTER_X, SCOREBOARD_CENTER_Y);
    this.drawScoreboardPanel();
    this.scoreboardRoot.addChild(this.scoreboardBg);
    this.scoreboardTitle.anchor.set(0.5, 0);
    this.scoreboardTitle.position.set(0, -SB_H / 2 + 10);
    this.scoreboardRoot.addChild(this.scoreboardTitle);
    this.scoreboardBlock.anchor.set(0.5, 0);
    this.scoreboardBlock.position.set(0, -SB_H / 2 + 32);
    this.scoreboardRoot.addChild(this.scoreboardBlock);
    this.scoreboardCountdown.anchor.set(0.5, 0);
    /* 블록 번호(2줄) 아래와 겹치지 않도록 여백 확보 */
    this.scoreboardCountdown.position.set(0, -SB_H / 2 + 100);
    this.scoreboardRoot.addChild(this.scoreboardCountdown);
    this.addChild(this.scoreboardRoot);

    this.boardAnchor.position.set(DISP_X, DISP_Y);
    this.drawFrameFallback();
  }

  private drawScoreboardPanel() {
    const g = this.scoreboardBg;
    g.clear();
    g.lineStyle(3, 0xfacc15, 0.75);
    g.beginFill(0x0f172a, 0.94);
    g.drawRoundedRect(-SB_W / 2, -SB_H / 2, SB_W, SB_H, 14);
    g.endFill();
    g.lineStyle(1, 0xfacc15, 0.35);
    g.drawRoundedRect(-SB_W / 2 + 3, -SB_H / 2 + 3, SB_W - 6, SB_H - 6, 12);
  }

  /**
   * ethminions.php `#waiting-scoreboard` 내용 동기화
   */
  updateScoreboard(content: {
    visible: boolean;
    blockLine: string;
    countdownLine: string;
  }) {
    this.scoreboardRoot.visible = content.visible;
    this.scoreboardBlock.text = content.blockLine;
    this.scoreboardCountdown.text = content.countdownLine;
  }

  private drawFrameFallback() {
    const g = this.frameFallback;
    g.clear();
    g.lineStyle(3, 0xf59e0b, 0.9);
    g.beginFill(0x1e293b, 0.5);
    g.drawRoundedRect(0, 0, ETH_FRAME_W, ETH_FRAME_H, 14);
    g.endFill();
  }

  mountBoard(board: PIXI.Container) {
    this.boardAnchor.removeChildren();
    this.boardAnchor.addChild(board);
  }

  applyTextures(t: MinionsTextureSet) {
    const apply = (sp: PIXI.Sprite, key: keyof MinionsTextureSet) => {
      const tex = t[key];
      if (tex?.valid) {
        sp.texture = tex;
        sp.visible = true;
        sp.alpha = 1;
      } else {
        sp.visible = false;
        sp.alpha = 0;
      }
    };

    apply(this.frameSpr, 'boxt');
    this.frameFallback.visible = !this.frameSpr.visible;
    if (this.frameSpr.visible) {
      placeSprite(this.frameSpr, 0, 0, ETH_FRAME_W, ETH_FRAME_H);
    }

    const railEnds = ladderRailEndsInParentRect(DISP_W, DISP_H);
    const frame = (p: { x: number; y: number }) => ({
      x: DISP_X + p.x,
      y: DISP_Y + p.y,
    });

    apply(this.leftSpr, 'left');
    if (this.leftSpr.visible) {
      const p = frame(railEnds.leftTop);
      placeGameButtonAtRailEnd(this.leftSpr, p.x, p.y, UNIFIED_GAME_BTN_BOX);
    }
    apply(this.rightSpr, 'right');
    if (this.rightSpr.visible) {
      const p = frame(railEnds.rightTop);
      placeGameButtonAtRailEnd(this.rightSpr, p.x, p.y, UNIFIED_GAME_BTN_BOX);
    }
    apply(this.oddSpr, 'odd');
    if (this.oddSpr.visible) {
      const p = frame(railEnds.bottomLeft);
      placeGameButtonAtRailEnd(this.oddSpr, p.x, p.y, UNIFIED_GAME_BTN_BOX);
    }
    apply(this.evenSpr, 'even');
    if (this.evenSpr.visible) {
      const p = frame(railEnds.bottomRight);
      placeGameButtonAtRailEnd(this.evenSpr, p.x, p.y, UNIFIED_GAME_BTN_BOX);
    }

    apply(this.soloSpr, 'solo');
    if (this.soloSpr.visible) {
      placeSprite(this.soloSpr, -10, ETH_FRAME_H - 130, 140, 120);
    }
    apply(this.teamSpr, 'team');
    if (this.teamSpr.visible) {
      placeSprite(
        this.teamSpr,
        ETH_FRAME_W - 130,
        ETH_FRAME_H - 130,
        140,
        120
      );
    }
  }

  getBoardDesignSize(): { w: number; h: number } {
    return { w: DISP_W, h: DISP_H };
  }
}
